import { c as _c } from "react/compiler-runtime";
import { RechartsArea as Area, RechartsLine as Line } from '@precis-digital/kurama/recharts';
import CustomScatter from '../components/drawing/CustomScatter';
import DynamicBar from '../components/drawing/DynamicBar';
import GradientLine from '../components/drawing/GradientLine';
import HoverablePie from '../components/drawing/HoverablePie';
import { type ReactElement } from 'react';
import type { DrawingElementProps, PolarHandlers } from '../internalTypes';
import type { ChartDataRow, DrawingColorMaps, DrawingInnerProps } from '../types';
import { assertNever } from '../utils/generics';
import { useChartCardDefaults } from './useChartCardDefaults';
const useDrawingElements = t0 => {
  const $ = _c(9);
  const {
    drawingElementsProps,
    drawingColorMaps,
    data,
    polarHandlers
  } = t0;
  const defaults = useChartCardDefaults();
  let drawingElements;
  if ($[0] !== data || $[1] !== defaults || $[2] !== drawingColorMaps.primary.area || $[3] !== drawingColorMaps.secondary || $[4] !== drawingElementsProps || $[5] !== polarHandlers?.hoveredIndex || $[6] !== polarHandlers?.onMouseEnter || $[7] !== polarHandlers?.onMouseLeave) {
    drawingElements = [];
    const needsSecondArea = drawingColorMaps.primary.area === "stroke" && drawingColorMaps.secondary.area === "fill";
    for (const {
      type,
      key,
      props
    } of drawingElementsProps) {
      bb0: switch (type) {
        case "bar":
        case "line":
        case "gradientLine":
        case "scatter":
        case "pie":
        case "donut":
          {
            break bb0;
          }
        case "area":
          {
            if (needsSecondArea) {
              const areaProps = {
                ...(props as DrawingInnerProps<'area'>),
                fillOpacity: 0
              };
              areaProps.stackId = `${areaProps.stackId}-line`;
              drawingElements.splice(0, 0, <Area key={`${key}-line`} {...areaProps} />);
            }
            break bb0;
          }
        default:
          {
            assertNever(type);
          }
      }
    }
    for (const {
      type: type_0,
      key: key_0,
      axisType,
      props: props_0
    } of drawingElementsProps) {
      bb1: switch (type_0) {
        case "bar":
          {
            drawingElements.push(<DynamicBar key={key_0} axisType={axisType} {...props_0 as DrawingInnerProps<'bar'>} />);
            break bb1;
          }
        case "line":
          {
            drawingElements.push(<Line key={key_0} {...props_0 as DrawingInnerProps<'line'>} />);
            break bb1;
          }
        case "gradientLine":
          {
            drawingElements.push(<defs><linearGradient id={`lineGradient-${props_0.dataKey as string}`} x1="0" y1="0" x2="0" y2="1">{defaults.specialDrawing.gradientLine.stops.map(stop => <stop key={stop.offset} offset={stop.offset} stopColor={props_0.fill} stopOpacity={stop.stopOpacity} />)}</linearGradient></defs>, <GradientLine key={key_0} {...props_0 as DrawingInnerProps<'area'>} />);
            break bb1;
          }
        case "area":
          {
            const areaProps_0 = {
              ...(props_0 as DrawingInnerProps<'area'>)
            };
            if (needsSecondArea) {
              areaProps_0.stroke = "none";
            }
            drawingElements.splice(0, 0, <Area key={key_0} {...areaProps_0} />);
            break bb1;
          }
        case "scatter":
          {
            drawingElements.push(<CustomScatter key={key_0} data={data} {...props_0} />);
            break bb1;
          }
        case "pie":
          {
            drawingElements.push(<HoverablePie key={key_0} data={data} onMouseEnter={polarHandlers?.onMouseEnter} onMouseLeave={polarHandlers?.onMouseLeave} hoveredIndex={polarHandlers?.hoveredIndex} {...props_0 as DrawingInnerProps<'pie'>} />);
            break bb1;
          }
        case "donut":
          {
            drawingElements.push(<HoverablePie key={key_0} data={data} onMouseEnter={polarHandlers?.onMouseEnter} onMouseLeave={polarHandlers?.onMouseLeave} hoveredIndex={polarHandlers?.hoveredIndex} {...props_0 as DrawingInnerProps<'donut'>} />);
            break bb1;
          }
        default:
          {
            assertNever(type_0);
          }
      }
    }
    $[0] = data;
    $[1] = defaults;
    $[2] = drawingColorMaps.primary.area;
    $[3] = drawingColorMaps.secondary;
    $[4] = drawingElementsProps;
    $[5] = polarHandlers?.hoveredIndex;
    $[6] = polarHandlers?.onMouseEnter;
    $[7] = polarHandlers?.onMouseLeave;
    $[8] = drawingElements;
  } else {
    drawingElements = $[8];
  }
  return drawingElements;
};
export default useDrawingElements;